/**
 * `/admin/roles` — the editable role / permission matrix.
 *
 * Every built-in role ships with a baseline permission bundle
 * (`packages/shared/src/permissions.ts`). This panel lets an admin
 * customise any non-`super_admin` bundle: the chosen set replaces the
 * baseline at session-build time, so the next access-token refresh
 * picks it up. `super_admin` is shown read-only — it always grants
 * every permission by definition, and locking it down here would be an
 * unrecoverable footgun (no one could edit roles back).
 *
 * Reads + writes go through the dashboard server's
 * `/api/admin/roles[...]` endpoints; the resolver in
 * `apps/server/src/services/sessionUser.ts` consults the same store
 * when minting tokens, so the matrix here is the single source of
 * truth for what every dashboard role grants.
 *
 * This file is the orchestrator: the load query, the save/reset
 * mutations, and the reset-confirmation. Each role renders as a
 * `roles-panel/RoleCard` (collapsible, with its own draft state); the
 * permission grouping lives in `roles-panel/permissionGroups`, the
 * subheader copy in `roleText`, and the checkbox grid + status chips in
 * `PermissionGrid` + `RoleStatusChips`.
 */
import { Box, Button, Card, CardContent } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type ListRolesResponse,
  type Permission,
  type Role,
  type RoleDefinition,
} from "@xop/dashboard-shared";
import { useState } from "react";

import { ConfirmDialog } from "../../components/ui/confirm-dialog.js";
import { PageLoader } from "@/components/ui/page-loader";
import { ApiError, api } from "../../lib/api.js";
import { useCan } from "../../auth/permissions.js";
import { ErrorBanner } from "./roles-panel/ErrorBanner.js";
import { RoleCard } from "./roles-panel/RoleCard.js";

export function RolesPanel(): JSX.Element {
  const canEdit = useCan("admin:permissions");
  const qc = useQueryClient();

  const {
    data,
    isLoading,
    isError,
    error: loadError,
    refetch,
  } = useQuery({
    queryKey: ["admin", "roles"],
    queryFn: () => api<ListRolesResponse>("/api/admin/roles"),
  });

  const save = useMutation({
    mutationFn: async ({
      role,
      permissions,
    }: {
      role: Role;
      permissions: Permission[];
    }) =>
      api(`/api/admin/roles/${role}/permissions`, {
        method: "PUT",
        body: { permissions },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "roles"] }),
  });

  const reset = useMutation({
    mutationFn: async (role: Role) =>
      api(`/api/admin/roles/${role}/permissions`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "roles"] }),
  });

  const [pendingReset, setPendingReset] = useState<RoleDefinition | null>(null);

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {isLoading && <PageLoader />}

      {isError && !isLoading && (
        <Card>
          <CardContent sx={{ px: 2.5, py: 2 }}>
            <ErrorBanner>
              <Box sx={{ fontWeight: 600 }}>Couldn&rsquo;t load the role matrix.</Box>
              <Box sx={{ fontSize: 12 }}>
                {loadError instanceof ApiError
                  ? loadError.message
                  : "Unknown error from /api/admin/roles."}
              </Box>
              <Box>
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  onClick={() => {
                    void refetch();
                  }}
                >
                  Retry
                </Button>
              </Box>
            </ErrorBanner>
          </CardContent>
        </Card>
      )}

      {data?.roles.map((role) => (
        <RoleCard
          key={role.role}
          role={role}
          canEdit={canEdit}
          isSaving={save.isPending && save.variables?.role === role.role}
          saveError={
            save.isError && save.variables?.role === role.role
              ? save.error
              : null
          }
          onSave={(permissions) =>
            save.mutate({ role: role.role, permissions })
          }
          onResetRequest={() => setPendingReset(role)}
        />
      ))}

      <ConfirmDialog
        isOpen={pendingReset !== null}
        onClose={() => setPendingReset(null)}
        title="Reset role to baseline?"
        description={
          pendingReset
            ? `Discards the customised permission set for "${pendingReset.label}" and restores the bundle defined in the source. Active users on this role will see the change on the next access-token refresh (≤30 min).`
            : ""
        }
        confirmLabel="Reset to baseline"
        variant="warning"
        onConfirm={async () => {
          if (!pendingReset) return;
          await reset.mutateAsync(pendingReset.role);
        }}
      />
    </Box>
  );
}
